import { Info, Minus, MessageSquare, SquareArrowOutUpRight, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { IconButton } from '@/components/ui/IconButton'
import { cn } from '@/lib/cn'
import { useSession } from '@/features/auth/api/useSession'
import { ChatPanel } from '@/features/cases/components/ChatPanel'
import { useChatSocketContext } from '@/features/chat/api/chatSocketContext'
import { useChatDockState } from '@/features/chat/api/useChatDockState'
import { QuoteArchiveBlock } from '@/features/chat/components/QuoteArchiveBlock'
import { CHAT_POLL_MS, newClientKey } from '@/features/cases/api/useCaseChat'
import {
  useConversation,
  useMarkRead,
  useSendMessage,
} from '@/features/chat/api/useConversation'
import type { ChatSubjectKind } from '@/types/domain'

/* Where a record's conversation LIVES (Story 8.3, FR-46, UX-DR-13, UX-DR-28,
 * 0051, 0052).
 *
 * Was CaseChat, and is now subject-agnostic because a quote has exactly the
 * same conversation with exactly the same shape: the panel takes a KIND and a
 * SUBJECT ID rather than a case id, and the server binds the kind to the mount
 * point of the route it ends up calling.
 *
 * Two shapes, one mount:
 *
 *   desktop (lg and up) — a DOCK anchored bottom-right. It has two states:
 *     expanded, which is the conversation, and away, which is a button carrying
 *     the unread count. The reader puts it away either by MINIMISING it, which
 *     is remembered across records and reloads, or by CLOSING it, which lasts as
 *     long as this record is open — see `showingDock` below for why both exist.
 *
 *     From xl up, an EXPANDED dock stands beside the record rather than over it
 *     (N-06, #127): it marks itself `data-chat-dock="expanded"`
 *     (DOCK_EXPANDED_SELECTOR) and AppShell's `<main>` pads itself by the dock's
 *     width while that marker is in the DOM, so the record reflows and nothing
 *     the reader can press sits under the conversation. Minimising or closing
 *     removes the marker and gives the width straight back. Between lg and xl
 *     the dock still floats over the corner: a reservation there would leave
 *     the record ~300px wide. The D1 pair below is untouched by any of this —
 *     the reservation follows the dock's state, it never decides it.
 *
 *   phone (below lg) — a FULL-SCREEN view, reached from the badge in the sticky
 *     header. A --spacing-chat-panel dock floating over a phone-width record
 *     covers the record and is still too narrow to read, so below the
 *     breakpoint chat takes the screen outright and gives it back on dismissal.
 *
 * ── THE DOCK IS DISMISSIBLE ON DESKTOP NOW, AND THIS FILE USED TO FORBID IT ──
 *
 * Until 0052 the desktop shape was a persistent right-hand column with no close
 * button, and the comment here said a close button "would be an invitation to
 * recreate the failure this feature prevents". That was FR-46 / UX-DR-13 read
 * as a rule about a CONTROL. It is not; it is a rule about an OUTCOME. The
 * incident behind it — D1, a reloading plan that changed and never reached the
 * truck manager — is a rule that nobody may be silently left out of a
 * coordination message. Forbidding the close button was one way to get there,
 * and it was the expensive way: it took roughly 306px off every record at every
 * width, on the busiest screens in the product, from every reader, forever,
 * including the ones reading a fifty-row money table who were not in the
 * conversation at all.
 *
 * What guarantees the outcome instead is a PAIR, and neither half works alone:
 *
 *   · THE DEFAULT IS EXPANDED (`useChatDockState`). A reader who never touches
 *     the control gets precisely the old behaviour — the conversation is on
 *     screen showing the last thing said, without having been asked for. The
 *     dock becomes hidden only by a deliberate act, performed by the one person
 *     who then knows it is hidden. That is the whole difference between this
 *     and the drawer the feature originally replaced, which was hidden by
 *     default and therefore hidden from people who had never thought about it.
 *
 *   · A COLLAPSED DOCK STILL SPEAKS. The floating button carries this reader's
 *     unread count for the record, taken from the conversation LIST row rather
 *     than from the thread — which is what lets it keep counting while the
 *     thread itself is unloaded and not polling. Somebody who minimised the
 *     chat to read the costing still learns that the reloading plan moved, in
 *     the corner of the same screen. This is the "not silently excluded from a
 *     coordination message" half of FR-46/FR-49, and it is why the count is a
 *     prop rather than something this component could decide to stop rendering.
 *
 * So the reference designs' close and minimise controls are implemented as
 * specified, and the intent they appeared to contradict is carried by the
 * default and by the badge instead. If a future change makes the dock start
 * collapsed, or drops the unread count from the button, it has reintroduced D1
 * and this comment is the record of why that is not a small tidy-up.
 *
 * ── ONE MOUNT ──
 *
 * The single most important mechanical thing about this file is that the two
 * shapes are the SAME ChatPanel with different positioning classes, rather than
 * a desktop panel and a phone panel. UX-DR-28 forbids a chat action that only
 * exists on desktop, and the reliable way to satisfy that is to make the two
 * shapes structurally incapable of diverging — the phone view cannot lack
 * paging, or the pill, or the composer, because there is no second
 * implementation for a feature to be missing from. Operations use this from the
 * field; a phone view that had drifted a release behind would be discovered by
 * somebody in a bonded warehouse, not by us.
 *
 * The cost of one mount is that the panel is mounted even while nothing is
 * showing it — a dismissed phone view, a collapsed desktop dock. That is paid
 * for deliberately: `useConversation` is handed `enabled`, so neither state
 * reads nor polls, and what stays alive is the composer's draft and the
 * reader's scroll position — which is exactly what a remount would throw away
 * every time somebody checked the Legs section or minimised the dock for a
 * minute.
 *
 * It does NOT decide who may see it. That is the caller's job through
 * canSeeRecordChat, because the pages already hold the record's owner id and
 * its team and this component would otherwise have to fetch them twice.
 */

/** Whether the layout is showing the floating dock rather than the full-screen
 *  view.
 *
 *  64rem is Tailwind's `lg`, and it is the same breakpoint CaseDetailPage gates
 *  its vertical section rail on — the point where the record has stopped being
 *  a single column of stacked cards and there is room for a dock in the
 *  corner. (From xl the shell also reserves the dock's width; see the header.)
 *
 *  Read in JS as well as in CSS, and the duplication is deliberate rather than
 *  sloppy: the CLASSES do the layout (which is what keeps the two shapes one
 *  mount), while this boolean answers a question CSS cannot — whether the
 *  conversation is being LOOKED AT, which is what decides whether it polls,
 *  whether a dismissed phone view should suppress the read, and whether the
 *  collapsed state means anything at all. A CSS-hidden panel is still a mounted
 *  panel making requests. */
const DESKTOP_QUERY = '(min-width: 64rem)'

function useIsDesktopChat(): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? false
      : window.matchMedia(DESKTOP_QUERY).matches,
  )

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(DESKTOP_QUERY)
    const onChange = () => setMatches(query.matches)
    query.addEventListener('change', onChange)
    onChange()
    return () => query.removeEventListener('change', onChange)
  }, [])

  return matches
}

/** Where `/chat` opens this conversation.
 *
 *  The tab is plural and the wire's kind is singular, so the mapping lives in
 *  one place rather than being spelled at the call site; `subject` is the
 *  contract ChatPage reads to pre-select the row. A link rather than a
 *  `navigate()` so the control is middle-clickable and shows its destination in
 *  the status bar, like every other way out of a record. */
function fullPagePath(kind: ChatSubjectKind, subjectId: string): string {
  const tab = kind === 'quote' ? 'quotes' : 'cases'
  return `/chat?tab=${tab}&subject=${encodeURIComponent(subjectId)}`
}

/** The marker an expanded desktop dock carries, as a CSS selector (N-06, #127).
 *
 *  AppShell's `<main>` reserves the dock's width with
 *  `xl:has-[[data-chat-dock=expanded]]:pr-…`, a class that has to be a literal
 *  for Tailwind to find it — so the selector is spelled twice, and this
 *  constant is what the tests on BOTH sides name it by. A rename on one side
 *  alone then fails a test instead of compiling to a rule that never matches. */
export const DOCK_EXPANDED_SELECTOR = '[data-chat-dock=expanded]'

export interface ChatDockProps {
  kind: ChatSubjectKind
  subjectId: string
  /** The conversation row's id, when one exists. Absent on a record nobody has
   *  spoken on yet: the socket has nothing to subscribe to until the first post
   *  creates the conversation and invalidates the list. */
  conversationId?: string
  /** The record's human number and client, for the dock's subtitle. Passed in
   *  rather than fetched: both pages already hold the record, and a panel that
   *  read it again would be a second request for a string. Optional because a
   *  caller may mount the dock before the detail has answered — a subtitle that
   *  says "undefined · undefined" for one frame is worse than no subtitle. */
  subjectNumber?: string
  subjectClient?: string
  /** This reader's unread count for the record, from the conversation list row.
   *
   *  Load-bearing rather than decorative — see the header. It is what a
   *  collapsed dock has instead of the conversation, and it must come from the
   *  LIST (which one request keeps current for every record) rather than from
   *  the thread, which a collapsed dock deliberately does not load. */
  unread?: number
  /** Whether the phone's full-screen view is showing. Owned by the PAGE rather
   *  than by this component, because the badge that opens it lives in the
   *  sticky header — one piece of state with two readers is better than a
   *  ref-passed imperative handle between two siblings. Ignored on desktop,
   *  where the reader's own remembered choice decides instead. */
  open: boolean
  onClose: () => void
  /** False on quotes: there is no quote file store, so a paperclip would upload
   *  bytes no message could ever point at. */
  allowAttachments?: boolean
  /** An archived thread refuses writes — a converted quote's conversation stays
   *  readable and stops accepting messages. */
  archived?: boolean
  /** A case's source quote (`CaseSummary.quoteId`). Its conversation is shown
   *  above the case's own, shut and read-only (S-1, O3 — QuoteArchiveBlock).
   *  Ignored on a quote's dock. */
  sourceQuoteId?: string
  className?: string
}

export function ChatDock({
  kind,
  subjectId,
  conversationId,
  subjectNumber,
  subjectClient,
  unread = 0,
  open,
  onClose,
  allowAttachments = false,
  archived = false,
  sourceQuoteId,
  className,
}: ChatDockProps) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const isDesktop = useIsDesktopChat()
  const socket = useChatSocketContext()
  const { collapsed, setCollapsed } = useChatDockState()

  /* MINIMISE AND CLOSE ARE NOT THE SAME BUTTON, and the reference designs give
     the dock both.

     `collapsed` is the REMEMBERED choice (`useChatDockState`): somebody who
     minimises is saying "I work with this in the corner", and it follows them
     to the next record and through a reload. `dismissed` is THIS VISIT only:
     somebody who closes is saying "not while I am reading this quote", and the
     next record opens expanded again.

     Two controls that both merely hid the dock would be dead UI — one of them
     would exist because the mockup drew it. This is the distinction that earns
     the second glyph, and it is the ordinary distinction between putting a
     window away and closing it.

     Neither can hide the floating button, which is what keeps both of them
     safe: whatever the reader pressed, the unread count is still in the corner.
     See the header. */
  const [dismissed, setDismissed] = useState(false)

  /* A different record is a different conversation, so a dismissal does not
     travel to it. The route param can change without this component
     unmounting — the detail pages stay mounted across a `:caseId` change — so
     the reset is explicit rather than left to a remount that may not happen.
     During render rather than in an effect, the pattern `useViewState`
     documents: an effect would paint one frame of the previous record's
     dismissal over this one. */
  const [lastSubject, setLastSubject] = useState(subjectId)
  if (subjectId !== lastSubject) {
    setLastSubject(subjectId)
    setDismissed(false)
  }

  /** Whether the desktop dock is showing the conversation rather than the
   *  floating button. Both flags have to be clear: a remembered minimise and a
   *  this-visit close put it in the same place. */
  const showingDock = !collapsed && !dismissed

  /* The conversation is READ when it is on screen: on desktop while the dock is
     showing, on a phone while the full-screen view is up. This is what stops a
     minimised dock and an unopened phone chat from polling a panel nobody is
     looking at — and it is `enabled` rather than a conditional mount for the
     reason in the header comment.

     The two halves are written out rather than folded into `isDesktop ?
     showingDock : open`, because they are two different facts: the dock's state
     means nothing below lg, and `open` means nothing above it. */
  const onScreen = (isDesktop && showingDock) || (!isDesktop && open)

  /* CHAT_POLL_MS, not the list's minute: Story 8.3 states a fifteen-second
     delivery bound for a record panel, and the socket is an accelerator rather
     than the mechanism — a proxy that refuses the upgrade must not turn that
     bound into a minute. */
  const thread = useConversation(kind, subjectId, onScreen, CHAT_POLL_MS)
  const send = useSendMessage(kind, subjectId)
  const markRead = useMarkRead()

  /* Watch this conversation while the panel is mounted (0029, AD-22).
   *
   * Refcounted inside the socket, so the chat page having the same thread open
   * does not cut this one off when it closes.
   *
   * Deliberately NOT gated on `onScreen`. A socket subscription costs the
   * server a room membership rather than a request per interval, and keeping it
   * while the dock is collapsed is what lets an arrival invalidate the
   * conversation list — which is where the collapsed dock's unread count comes
   * from. Cutting it would make the badge go quiet exactly when it is the only
   * thing speaking. */
  useEffect(() => {
    if (!conversationId) return
    socket.subscribe(conversationId)
    return () => socket.unsubscribe(conversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  /* Move the watermark to whatever is on screen. Fired on the last message's id
     rather than a scroll position: the server refuses to move a watermark
     backwards, so reporting the newest held message is idempotent.

     A PENDING bubble is skipped. Its id is the client key, which names no row
     the server has — reporting it would spend a request to earn a 404 on every
     message this reader sends. The effect re-fires with the real id the moment
     the send resolves, so nothing is lost by waiting. */
  const newest = thread.messages[thread.messages.length - 1]
  const lastSeen = useRef<string | null>(null)
  useEffect(() => {
    if (!conversationId || !newest || !onScreen) return
    if (newest.pending) return
    if (lastSeen.current === newest.id) return
    lastSeen.current = newest.id
    markRead.mutate({ conversationId, messageId: newest.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest?.id, newest?.pending, conversationId, onScreen])

  /* Escape dismisses the phone view. Bound on the document rather than the
     panel because focus is usually inside the composer's textarea, and a
     handler on the container would never see the key from there.

     Desktop is exempt, and now for a smaller reason than it used to be: the
     dock has a close button and a minimise button of its own, so Escape would
     be a third way to do the same thing — and a key that emptied the composer's
     draft while somebody was typing into it is a worse trade there than on a
     phone, where Escape is the only way back out of a full-screen view. */
  useEffect(() => {
    if (!open || isDesktop) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, isDesktop, onClose])

  /* The page behind a full-screen overlay must not scroll under it. Restoring
     the previous value rather than clearing it keeps an outer lock intact —
     the same rule Drawer follows, for the same reason.

     Phone only. The desktop dock stands beside the record (or, between lg and
     xl, over one corner of it) and the record is still the thing being read,
     so locking the page would be taking the record away to show a
     conversation next to it. */
  useEffect(() => {
    if (!open || isDesktop) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open, isDesktop])

  const handleSend = (draft: { body: string }) => {
    const body = draft.body.trim()
    if (!body) return
    /* The idempotency key is minted HERE, once per message, and rides the
       mutation. Not inside the mutation function, which re-runs on a retry: a
       key regenerated per attempt is not an idempotency key at all, it is a
       guarantee of duplicates on exactly the flaky connection it exists for. */
    send.mutate({ body, clientKey: newClientKey() })
  }

  /* What the dock is about, under its title. Both facts or the number alone —
     never the template with a hole in it, which is what a record whose detail
     has not answered yet would produce. */
  const subtitle =
    subjectNumber && subjectClient
      ? t('cases.chat.dock.subject', {
          number: subjectNumber,
          client: subjectClient,
        })
      : (subjectNumber ?? null)

  return (
    <>
      <div
        className={cn(
          /* Below lg: a full-screen fixed layer, shown only when opened.
             z-50 matches Drawer's, so the two overlays cannot half-cover each
             other; the sticky header is z-20 and is deliberately underneath,
             because a full-screen chat that left the record header showing
             above it would not be full-screen. */
          'bg-surface fixed inset-0 z-50 flex-col',
          open ? 'flex' : 'hidden',

          /* Above lg: a card in the bottom-right corner. `inset-auto` undoes
             `inset-0` — every `lg:` here resets one part of the phone shape
             rather than a second element existing for the desktop case.

             From xl up it stands in the width AppShell's `<main>` reserves for
             it while `data-chat-dock="expanded"` is set below (N-06), so it is
             a PANEL beside the record rather than something lifted over it —
             and a panel carries no shadow (frontend/README.md, the tokens.css
             elevation note). Between lg and xl nothing is reserved and it still
             floats over the corner, so the shadow stays there: there it IS an
             overlay, and the edge is what separates it from the record below.
             The floating button keeps its shadow at every width for the same
             reason.

             z-40 rather than z-50, and this is the one place the two shapes
             deliberately differ in stacking. The dock is furniture beside (or,
             below xl, over) a record the reader is still using, so it must be
             ABOVE the sticky header (z-20) and BELOW every modal overlay: Drawer and
             Sheet both portal to <body> at z-50, and a status-send sheet
             half-covered by a chat panel would be a form somebody submits
             without having read it. Tying at 50 and letting DOM order decide
             would work today and break the first time something renders after
             this. The phone layer keeps z-50 because it IS the screen.

             The height is capped rather than fixed so the dock never runs off a
             13" laptop: it stops short of the topbar above and of the corner
             below, and the ChatPanel inside scrolls its own transcript. */
          !showingDock
            ? 'lg:hidden'
            : cn(
                'lg:flex lg:inset-auto lg:right-6 lg:bottom-6 lg:z-40',
                'lg:h-[min(36rem,calc(100dvh-var(--spacing-topbar)-4rem))]',
                'lg:w-[var(--spacing-chat-panel)] lg:max-w-[calc(100vw-3rem)]',
                'lg:border-line lg:rounded-[var(--radius-lg)] lg:border lg:shadow-lg',
                'xl:shadow-none',
              ),
          className,
        )}
        /* The reservation's marker (N-06, DOCK_EXPANDED_SELECTOR). Derived from
           the same two booleans that pick the classes above, so it is not a
           second piece of state: present exactly while the desktop dock shows
           the conversation, absent minimised, closed and on a phone — where
           `open` decides instead and the full-screen layer covers the record
           outright. AppShell pads `<main>` by it; nothing reads it in JS. */
        data-chat-dock={isDesktop && showingDock ? 'expanded' : undefined}
        /* Hidden from assistive technology whenever nothing is showing it, so a
           screen reader is not walking a conversation that is not on screen —
           a dismissed phone view or a collapsed dock. The floating button is a
           SIBLING of this element rather than a child precisely so that it
           stays reachable while this is inert. */
        aria-hidden={!onScreen}
        /* A plain boolean prop rather than a spread of `inert: ''`. React 19
           understands `inert` natively and types it as a boolean, and it omits
           the attribute entirely when false — which is the same DOM the spread
           produced, without the string that no longer typechecks. */
        inert={!onScreen}
      >
        {/* The header exists at both widths and says the same things, which is
            the one-mount rule applied to the chrome as well as to the panel. It
            names the record because a floating card over a page needs to say
            which conversation it is — and on a phone it is also the way back
            out, which is why the close button is not the desktop-only control
            here. Minimise is: a phone's collapsed state is the badge in the
            sticky header, not a button hovering over the record. */}
        <div className="border-border-subtle flex shrink-0 items-start gap-2.5 border-b px-4 py-3">
          <MessageSquare
            size={16}
            aria-hidden
            className="text-text-muted mt-0.5 shrink-0"
          />

          <div className="min-w-0 flex-1">
            <h2 className="text-text-heading truncate text-[14px] font-normal tracking-[-0.2px]">
              {kind === 'quote'
                ? t('cases.chat.dock.quote')
                : t('cases.chat.dock.case')}
            </h2>
            <div className="mt-0.5 flex min-w-0 items-center gap-2">
              {subtitle ? (
                <span className="text-text-muted asa-num min-w-0 truncate text-[12px]">
                  {subtitle}
                </span>
              ) : null}
              {/* Said beside the title as well as in the banner below. The chip
                  is for the reader who already knows and is scanning; the
                  banner is for the one who does not. External participants are
                  structurally absent from this surface (FR-46) — nothing typed
                  here can reach a client — and that is worth over-saying. */}
              <span className="bg-attention-soft text-attention shrink-0 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10.5px] leading-none font-semibold">
                {t('cases.chat.dock.internal')}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-0.5">
            {/* Out of the corner and onto the full page, with this conversation
                already selected. A Link rather than a button: it is a
                navigation, and a reader who middle-clicks it should get the
                tab they expected. Styled to match the two IconButtons beside
                it rather than borrowing their component, which renders a
                <button> and has no asChild escape. */}
            <Link
              to={fullPagePath(kind, subjectId)}
              aria-label={t('cases.chat.dock.expand')}
              title={t('cases.chat.dock.expand')}
              className={cn(
                'inline-flex size-7 shrink-0 items-center justify-center rounded-md',
                'text-text-muted hover:bg-surface-hover hover:text-text-heading',
                'pointer-coarse:size-11 transition-colors',
                'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
              )}
            >
              <SquareArrowOutUpRight size={15} aria-hidden />
            </Link>

            {/* Desktop only. Below lg the collapsed state is ChatDockBadge in
                the sticky header — there is no corner for a button to float
                in, and a phone that minimised a full-screen view to a floating
                circle would be covering the record it just gave back. */}
            <IconButton
              icon={Minus}
              label={t('cases.chat.dock.minimise')}
              onClick={() => setCollapsed(true)}
              size="sm"
              className="max-lg:hidden"
            />

            <IconButton
              icon={X}
              label={t('common.actions.close')}
              /* One control, one meaning, spelled differently at each width
                 because the two shapes are opened by different things. On a
                 phone it dismisses the full-screen view the PAGE opened, so it
                 hands the state back to the page. On desktop it closes the dock
                 for this record only — unlike minimise beside it, nothing is
                 remembered and the next record opens expanded. Both are
                 recoverable in one press: the header badge, or the floating
                 button. */
              onClick={() => {
                if (isDesktop) setDismissed(true)
                else onClose()
              }}
              size="sm"
            />
          </div>
        </div>

        {/* Who can read this. A banner rather than a line of help text under
            the composer: it has to be seen before something is typed, not
            after. Same words and same tint as the chat page's, because it is
            the same promise about the same conversation. */}
        <div className="bg-attention-soft text-attention flex shrink-0 items-center gap-2 px-4 py-2 text-[12px]">
          <Info size={14} aria-hidden className="shrink-0" />
          <span className="min-w-0">{t('cases.chat.dock.teamOnly')}</span>
        </div>

        {/* The conversation that led to this case, from its quote (S-1, O3).
            Between the banner and the thread: above the composer, so it is
            never mistaken for part of the live conversation, and shut, so the
            live one keeps the dock. Asked for only while the dock is on screen
            — this component is mounted only for a reader the page lets see the
            case chat (`canSeeRecordChat`). A case only: a quote's dock IS that
            conversation. */}
        {kind === 'case' ? (
          <QuoteArchiveBlock
            caseId={subjectId}
            quoteId={sourceQuoteId}
            enabled={onScreen}
            className="mx-3 mt-3 shrink-0"
          />
        ) : null}

        <ChatPanel
          messages={thread.messages}
          currentUserId={user?.id ?? ''}
          currentUserAvatarUrl={user?.avatarUrl}
          onSend={handleSend}
          isPending={thread.isLoading && onScreen}
          isError={thread.isError}
          hasMore={thread.hasMore}
          isLoadingMore={thread.isLoadingMore}
          onLoadMore={thread.loadEarlier}
          /* An archived thread is read-only, so it offers no paperclip even on
             a case: the composer is the only thing that could act on a picked
             file, and a tray that can never be sent is a trap. */
          allowAttachments={allowAttachments && !archived}
          className="min-h-0 flex-1"
        />
      </div>

      {/* The collapsed state, and the reason a close button is affordable at
          all. Rendered only on desktop and only while the dock is away —
          conditionally rather than with `lg:` classes, because a hidden button
          is still a tab stop and a phone must not have two ways into the same
          chat (ChatDockBadge is its one way). There is no first-paint flicker
          to pay for: `useIsDesktopChat` reads matchMedia in its state
          initialiser, so the very first render already knows which shape it
          is. */}
      {isDesktop && !showingDock ? (
        <ChatDockFab
          unread={unread}
          /* Clears BOTH ways the dock can be away. A reader who pressed the
             button wants the conversation, and making them press it twice
             because they had once minimised and once closed would be the code's
             bookkeeping leaking into the interface. */
          onOpen={() => {
            setCollapsed(false)
            setDismissed(false)
          }}
        />
      ) : null}
    </>
  )
}

/** The minimised dock: a floating button carrying the unread count.
 *
 *  This is the half of the FR-46 guarantee that survives the close button — see
 *  this file's header. It is not decoration and it is not a launcher: it is the
 *  thing that tells somebody reading a costing table that the reloading plan
 *  moved. The count comes from the conversation list, which stays current for
 *  every record on one request, rather than from the thread, which is
 *  deliberately unloaded while the dock is collapsed.
 */
function ChatDockFab({ unread, onOpen }: { unread: number; onOpen: () => void }) {
  const { t } = useTranslation()

  return (
    /* `group` on the wrapper rather than on the button, so the label reacts to
       hover and to keyboard focus on the button beside it. z-40 for the same
       reason the expanded dock uses it: over the record, under every modal. */
    <div className="group fixed right-6 bottom-6 z-40 flex items-center gap-2.5">
      {/* The label, on hover and on focus. `aria-hidden` because the button
          below already carries these words as its accessible name — announcing
          them twice is noise, and the pill is a sighted-hover affordance. It
          never takes pointer events, so it cannot eat a click aimed at the
          corner of the button. */}
      <span
        aria-hidden
        className={cn(
          'bg-surface border-line text-text-body pointer-events-none rounded-md border',
          'px-2.5 py-1.5 text-[12.5px] whitespace-nowrap shadow-sm',
          'translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100',
          'group-focus-within:translate-x-0 group-focus-within:opacity-100',
          'transition-[opacity,transform] duration-[var(--dur-fast)]',
          'ease-[var(--ease-standard)]',
        )}
      >
        {t('cases.chat.dock.open')}
      </span>

      <button
        type="button"
        onClick={onOpen}
        aria-label={
          unread > 0
            ? t('cases.chat.dock.openWithUnread', { count: unread })
            : t('cases.chat.dock.open')
        }
        className={cn(
          'bg-brand text-text-on-brand relative flex size-14 cursor-pointer',
          'items-center justify-center rounded-full shadow-lg',
          'hover:bg-brand/90 transition-[background-color,transform]',
          'duration-[var(--dur-fast)] ease-[var(--ease-standard)] active:scale-95',
        )}
      >
        <MessageSquare size={22} aria-hidden />
        {unread > 0 ? (
          /* NEGATIVE rather than the brand tint the phone badge uses: this
             badge sits ON a brand-filled circle, and brand on brand is not a
             badge. Ringed in the surface colour so it reads as a chip on the
             button rather than a hole punched through it. The server caps the
             count at 99, so the hundredth is "99+" rather than a number nobody
             reads to the end of. */
          <span
            className={cn(
              'bg-negative text-text-on-brand asa-num ring-surface absolute -top-0.5 -right-0.5',
              'flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5',
              'text-[11px] leading-none font-semibold ring-2',
            )}
          >
            {unread >= 99 ? t('chat.unreadCapped') : unread}
          </span>
        ) : null}
      </button>
    </div>
  )
}

/** The phone's way in: a badge in the sticky header (UX-DR-28).
 *
 *  `lg:hidden` rather than conditionally rendered, so it costs no JS breakpoint
 *  read and cannot flicker on the first paint the way a `useIsDesktopChat()`
 *  guard here would. Above the breakpoint the dock's own floating button is the
 *  collapsed state instead.
 *
 *  The count is this reader's unread total for the record's conversation, taken
 *  from the conversation list's row rather than counted locally — so a phone
 *  user reading the Legs section still learns that somebody said something,
 *  which is the "not silently excluded from a coordination message" half of the
 *  AC (FR-46, FR-49). It is passed in rather than read here, because the badge
 *  and the panel must count the same thing.
 */
export function ChatDockBadge({
  onOpen,
  unread,
}: {
  onOpen: () => void
  unread: number
}) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={
        unread > 0
          ? t('cases.chat.openWithUnread', { count: unread })
          : t('cases.chat.label')
      }
      title={t('cases.chat.label')}
      className={cn(
        'border-line text-text-muted hover:text-text-heading hover:bg-surface-hover',
        'relative flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border',
        'pointer-coarse:size-11 transition-colors duration-[var(--dur-fast)]',
        'ease-[var(--ease-standard)] lg:hidden',
      )}
    >
      <MessageSquare size={16} aria-hidden />
      {unread > 0 ? (
        /* Ringed in the page background so the badge reads as a chip on the
           control rather than a hole punched through it. The server caps the
           count at 99, so the hundredth is "99+" rather than a number nobody
           reads to the end of. */
        <span
          className={cn(
            'bg-brand text-text-on-brand asa-num ring-surface absolute -top-1 -right-1',
            'flex min-w-4 items-center justify-center rounded-full px-1 py-0.5',
            'text-[10px] leading-none font-semibold ring-2',
          )}
        >
          {unread >= 99 ? t('chat.unreadCapped') : unread}
        </span>
      ) : null}
    </button>
  )
}
